import { motion } from 'framer-motion';
import React from 'react';
import { FiArrowLeft, FiBarChart2, FiCpu, FiDatabase, FiGitBranch, FiServer, FiSettings } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import Navbar from '../../components/Navbar';

const EnterpriseSolutions = () => {
  const solutions = [
    {
      title: "Custom SaaS Product Development",
      description: "End-to-end SaaS platform development with scalable architecture, modern tech stack, and seamless user experiences.",
      icon: FiServer,
      features: ["Multi-tenant Architecture", "API-first Design", "Scalable Infrastructure", "Real-time Analytics"]
    },
    {
      title: "AI Agents for Business Process Automation",
      description: "Intelligent automation solutions that streamline workflows, reduce manual tasks, and enhance operational efficiency.",
      icon: FiCpu,
      features: ["Process Mining", "Workflow Automation", "Intelligent Decision Making", "Performance Optimization"]
    },
    {
      title: "Enterprise Data Platform Engineering",
      description: "Comprehensive data platforms that unify, process, and analyze enterprise data for actionable insights.",
      icon: FiDatabase,
      features: ["Data Lake Architecture", "Real-time Processing", "Advanced Analytics", "Data Governance"]
    },
    {
      title: "Digital Twin & Simulation Modeling",
      description: "Virtual representations of physical systems enabling predictive maintenance and optimization.",
      icon: FiSettings,
      features: ["Real-time Monitoring", "Predictive Analytics", "Scenario Simulation", "Performance Optimization"]
    }
  ];

  const caseStudies = [
    {
      title: "Fortune 500 Digital Transformation",
      challenge: "Large manufacturing company needed to modernize legacy systems and implement AI-driven automation",
      solution: "Built comprehensive digital platform with AI agents for process automation and real-time analytics",
      results: ["40% reduction in operational costs", "60% improvement in process efficiency", "90% faster decision-making"]
    },
    {
      title: "Enterprise SaaS Platform Development",
      challenge: "Tech company required scalable SaaS platform to serve 10,000+ enterprise customers",
      solution: "Developed cloud-native multi-tenant platform with advanced analytics and integration capabilities",
      results: ["Serving 15,000+ active users", "99.9% platform uptime", "200% increase in customer retention"]
    }
  ];

  const industries = [
    { name: "Manufacturing", percentage: 35 },
    { name: "Financial Services", percentage: 25 },
    { name: "Healthcare", percentage: 20 },
    { name: "Technology", percentage: 20 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-green-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-20 relative overflow-hidden">
        <div className="relative h-96 md:h-[500px] bg-gradient-to-br from-green-600 to-emerald-600">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-green-600/80 to-emerald-600/60"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <div className="flex items-center space-x-4 text-white/80 text-sm font-body">
                  <Link 
                    to="/expertise"
                    className="flex items-center space-x-2 hover:text-white transition-colors duration-300"
                  >
                    <FiArrowLeft size={16} />
                    <span>Back to Expertise</span>
                  </Link>
                  <span>•</span>
                  <span className="px-3 py-1 bg-white/20 rounded-full font-accent tracking-wide">
                    Enterprise Solutions
                  </span>
                </div>
                
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <FiServer className="text-white" size={32} />
                  </div>
                  <div>
                    <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight font-display tracking-tight">
                      Enterprise Solutions
                    </h1>
                    <p className="text-xl text-green-100 font-body font-light tracking-wide">
                      Empowering Large-Scale Digital Transformation
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6 font-accent tracking-wide">
                Enterprise Innovation
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
                Scaling
                <span className="block bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  Enterprise Success
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 font-body font-light leading-relaxed">
                We deliver comprehensive enterprise solutions that help large organizations improve productivity, 
                automate complex workflows, and scale efficiently through intelligent technology and strategic implementation.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-green-100">
                  <div className="text-3xl font-bold text-green-600 font-display">50+</div>
                  <div className="text-sm text-gray-600 font-body">Enterprise Projects</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-green-100">
                  <div className="text-3xl font-bold text-emerald-600 font-display">500k+</div>
                  <div className="text-sm text-gray-600 font-body">Users Served</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-green-100">
                  <div className="text-3xl font-bold text-green-600 font-display">95%</div>
                  <div className="text-sm text-gray-600 font-body">Project Success Rate</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-lg border border-green-100">
                  <div className="text-3xl font-bold text-emerald-600 font-display">24/7</div>
                  <div className="text-sm text-gray-600 font-body">Support Coverage</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {[
                { icon: FiGitBranch, title: "Agile Transformation", desc: "Modern development practices for faster delivery and better quality" },
                { icon: FiBarChart2, title: "Change Management", desc: "Strategic guidance for organizational transformation and adoption" },
                { icon: FiCpu, title: "AI-Powered Automation", desc: "Intelligent solutions that scale with your business needs" }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-2xl shadow-lg border border-green-100">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 font-heading tracking-wide">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 font-body leading-relaxed">
                      {benefit.desc}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Focus Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Industry
              <span className="block bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Experience
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Our enterprise solutions span across multiple industries, delivering specialized expertise for each sector's unique challenges.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl border border-green-100"
              >
                <div className="relative w-24 h-24 mx-auto mb-4">
                  <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 36 36">
                    <path
                      d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="2"
                    />
                    <path
                      d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
                      fill="none"
                      stroke="url(#greenGradient)"
                      strokeWidth="2"
                      strokeDasharray={`${industry.percentage}, 100`}
                    />
                    <defs>
                      <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#059669" />
                        <stop offset="100%" stopColor="#10b981" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-green-600 font-display">{industry.percentage}%</span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 font-heading tracking-wide">
                  {industry.name}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Enterprise
              <span className="block bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Solutions Portfolio
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Comprehensive technology solutions designed to accelerate enterprise growth and operational excellence.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group p-8 bg-white rounded-3xl border border-green-100 hover:shadow-2xl transition-all duration-500"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <solution.icon className="text-white" size={28} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 font-heading tracking-wide">
                      {solution.title}
                    </h3>
                    <p className="text-gray-700 font-body leading-relaxed">
                      {solution.description}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {solution.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full"></div>
                      <span className="text-sm text-gray-700 font-body">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Enterprise
              <span className="block bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Success Stories
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Real-world examples of how our enterprise solutions have transformed large-scale operations and driven measurable results.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-3xl border border-green-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-heading tracking-wide">
                  {study.title}
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 font-heading tracking-wide">Challenge</h4>
                    <p className="text-gray-600 font-body leading-relaxed">{study.challenge}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 font-heading tracking-wide">Solution</h4>
                    <p className="text-gray-600 font-body leading-relaxed">{study.solution}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 font-heading tracking-wide">Results</h4>
                    <div className="space-y-2">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full"></div>
                          <span className="text-gray-700 font-body">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-600 to-emerald-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/5"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display tracking-tight">
              Ready to Scale Your Enterprise?
            </h2>
            <p className="text-xl text-green-100 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Let's discuss how our enterprise solutions can help you modernize operations, automate processes, and drive sustainable growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-green-600 px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
                >
                  Start Your Enterprise Project
                </motion.button>
              </Link>
              <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-green-600 transition-all duration-300 font-accent tracking-wide"
              >
                Schedule Consultation
              </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EnterpriseSolutions; 